export interface EhrTemplate {
  readonly id: string
  readonly name: string
  readonly description: string
  readonly configJson: Record<string, unknown>
}

// Derived from packages/hl7-middleware/src/config/references/pcc-opentext.json
// Credentials stripped (set to empty strings) — fill in via transport fields.
const pccOpentextTemplate: EhrTemplate = {
  id: 'pcc-opentext',
  name: 'PCC / OpenText',
  description: 'PointClickCare with OpenText SFTP delivery — HL7 2.3, password auth, PDF attachment support.',
  configJson: {
    transport: {
      type: 'sftp',
      host: '',
      port: 22,
      authType: 'password',
      username: '',
      credential: '',
      inboxPath: '/inbox',
      outboxPath: '/outbox',
      archivePath: '/archive',
      pollIntervalSeconds: 60,
      filePattern: '*.hl7',
    },
    hl7: {
      version: '2.3',
      encoding: '^~\\&',
      segmentTerminator: '\r',
      segmentOrder: ['MSH', 'PID', 'ORC', 'OBR', 'OBX'],
      segments: {
        MSH: {
          sendingApplication: 'MedFlow',
          receivingApplication: 'PCCReceiver',
          messageType: 'ORU^R01',
          processingId: 'P',
        },
        SFT: { enabled: false },
        PID: {
          dateOfBirthFormat: 'YYYYMMDD',
          dateOfBirthInputFormat: 'MMDDYYYY',
          nameFieldPosition: 5,
          dobFieldPosition: 7,
          sexFieldPosition: 8,
          addressFieldPosition: 11,
          phoneFieldPosition: 13,
          includePhone: true,
        },
        ORC: {
          orderControl: 'RE',
          orderStatus: 'CM',
          includeEnteredBy: false,
          includeOrderingProvider: false,
          includeLicenseNo: false,
          includeOrderingFacility: false,
          includeOrderingFacilityAddress: false,
          includePatientAddress: false,
          includeLabName: true,
        },
        OBR: {
          placerOrderSource: 'order.externalID',
          universalServiceIdFormat: '{code}^{name}',
          resultStatusMapping: { Processed: 'F', Partial: 'P' },
          legacyResultStatusPosition: true,
          totalFields: 44,
          useTestCodeOverrides: true,
        },
        OBX: {
          valueTypeMapping: { Qualitative: 'TX', Quantitative: 'TX', default: 'TX' },
          identifierFormat: '{csvName}^{label}',
          valueFormat: '{value}',
          abnormalFlagRules: [],
          resultStatusDefault: 'F',
          includePerformingOrg: false,
          includePerformingOrgAddress: false,
          includeDateTimeOfAnalysis: true,
          dateTimeOfAnalysisPosition: 12,
          includeLabel: true,
          labelPosition: 16,
          pdfAttachment: { enabled: true, identifierCode: 'RPDF^Attachment', encodingFormat: 'MTL^PDF^PDF^Base64' },
        },
        SPM: { enabled: false },
      },
    },
    operations: {
      receiveOrders: { enabled: true, messageType: 'ORM^O01' },
      sendResults: { enabled: true, messageType: 'ORU^R01', triggerOn: ['finalize'] },
      archiveOrders: { enabled: true },
      cancelOrders: { enabled: true },
      correctedResults: { enabled: false },
    },
    codeMappings: {
      testCodes: {},
      specimenTypes: {},
      resultFlags: {},
    },
    facilities: [],
  },
}

// Derived from packages/hl7-middleware/src/config/references/ecw.json
// Credentials stripped — fill in via transport fields.
const ecwTemplate: EhrTemplate = {
  id: 'ecw',
  name: 'eClinicalWorks',
  description: 'eCW SFTP with PEM key auth — HL7 2.5.1, LOINC identifiers, corrected results enabled.',
  configJson: {
    transport: {
      type: 'sftp',
      host: '',
      port: 22,
      authType: 'pem',
      username: '',
      credential: '',
      inboxPath: '/hl7/inbox',
      outboxPath: '/hl7/outbox',
      archivePath: '/hl7/archive',
      pollIntervalSeconds: 30,
      filePattern: '*.hl7',
    },
    hl7: {
      version: '2.5.1',
      encoding: '^~\\&',
      segmentTerminator: '\r',
      segmentOrder: ['MSH', 'SFT', 'PID', 'ORC', 'OBR', 'OBX'],
      segments: {
        MSH: {
          sendingApplication: 'MedFlow',
          receivingApplication: 'eCW',
          messageType: 'ORU^R01^ORU_R01',
          processingId: 'P',
        },
        SFT: { enabled: true, vendorOrganization: 'Medflow', softwareVersion: '1.0', productName: 'Medflow' },
        PID: {
          dateOfBirthFormat: 'YYYYMMDD',
          dateOfBirthInputFormat: 'MMDDYYYY',
          nameFieldPosition: 5,
          dobFieldPosition: 7,
          sexFieldPosition: 8,
          addressFieldPosition: 11,
          phoneFieldPosition: 13,
          includePhone: true,
          totalFields: 21,
        },
        ORC: {
          orderControl: 'RE',
          orderStatus: 'CM',
          includeEnteredBy: true,
          includeOrderingProvider: true,
          includeLicenseNo: false,
          includeOrderingFacility: false,
          includeOrderingFacilityAddress: false,
          includePatientAddress: false,
        },
        OBR: {
          universalServiceIdFormat: '{code}^{name}^L',
          resultStatusMapping: { Processed: 'F', Partial: 'P', Corrected: 'C' },
          legacyResultStatusPosition: false,
          diagnosticServiceSectionId: 'LAB',
          totalFields: 46,
        },
        OBX: {
          valueTypeMapping: { Qualitative: 'ST', Quantitative: 'NM', default: 'ST' },
          identifierFormat: '{loincCode}^{label}^LN',
          valueFormat: '{value}',
          abnormalFlagRules: [
            { condition: 'valueEquals', matchValue: 'Not Detected', flag: 'N' },
            { condition: 'default', flag: 'A' },
          ],
          resultStatusDefault: 'F',
          includePerformingOrg: false,
          includeDateTimeOfAnalysis: true,
          dateTimeOfAnalysisPosition: 19,
          pdfAttachment: { enabled: false },
        },
        SPM: { enabled: false },
      },
    },
    operations: {
      receiveOrders: { enabled: true, messageType: 'ORM^O01' },
      sendResults: { enabled: true, messageType: 'ORU^R01', triggerOn: ['finalize'] },
      archiveOrders: { enabled: true },
      cancelOrders: { enabled: true },
      correctedResults: { enabled: true },
    },
    codeMappings: { testCodes: {}, specimenTypes: {}, resultFlags: {} },
    facilities: [],
  },
}

const genericSftpTemplate: EhrTemplate = {
  id: 'generic-sftp',
  name: 'Generic SFTP',
  description: 'Minimal SFTP skeleton — HL7 2.5.1 defaults, no code mappings. Use for any SFTP-based EHR.',
  configJson: {
    transport: {
      type: 'sftp',
      host: '',
      port: 22,
      authType: 'password',
      username: '',
      credential: '',
      inboxPath: '/inbox',
      outboxPath: '/outbox',
      archivePath: '/archive',
      pollIntervalSeconds: 60,
      filePattern: '*.hl7',
    },
    hl7: {
      version: '2.5.1',
      encoding: '^~\\&',
      segmentTerminator: '\r',
      segmentOrder: ['MSH', 'PID', 'ORC', 'OBR', 'OBX'],
      segments: {
        MSH: { sendingApplication: 'MedFlow', receivingApplication: '', messageType: 'ORU^R01', processingId: 'P' },
      },
    },
    operations: {
      receiveOrders: { enabled: false },
      sendResults: { enabled: true, messageType: 'ORU^R01', triggerOn: ['finalize'] },
    },
    codeMappings: { testCodes: {}, specimenTypes: {}, resultFlags: {} },
    facilities: [],
  },
}

const genericSoapTemplate: EhrTemplate = {
  id: 'generic-soap',
  name: 'Generic SOAP',
  description: 'Minimal SOAP/web-service skeleton — use for EHRs that accept results via a WSDL endpoint.',
  configJson: {
    transport: {
      type: 'soap',
      endpoint: '',
      wsdlUrl: '',
      auth: { type: 'credentials', username: '', credential: '' },
    },
    hl7: {
      version: '2.5.1',
      encoding: '^~\\&',
      segmentTerminator: '\r',
      segmentOrder: ['MSH', 'PID', 'ORC', 'OBR', 'OBX'],
      segments: {
        MSH: { sendingApplication: 'MedFlow', receivingApplication: '', messageType: 'ORU^R01', processingId: 'P' },
      },
    },
    operations: {
      receiveOrders: { enabled: false },
      sendResults: { enabled: true, messageType: 'ORU^R01', triggerOn: ['finalize'] },
    },
    codeMappings: { testCodes: {}, specimenTypes: {}, resultFlags: {} },
    facilities: [],
  },
}

export const EHR_TEMPLATES: readonly EhrTemplate[] = [
  pccOpentextTemplate,
  ecwTemplate,
  genericSftpTemplate,
  genericSoapTemplate,
]
